import React, {useEffect} from 'react';
import {useDispatch} from 'react-redux';

import {ErrorBoundary} from '@sentry/react';
import {ProfileTableHeader} from 'src/components/profile-tab-structure';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {NoResultPage} from 'src/modules/entitlements';
import {ErrorPage} from 'src/modules/error-page/error-page.components';

import {BoxLoading} from '@pb/kit-component-box-loading';

import {GeneratedUsageReportsTable} from './generated-usage-reports-table';
import {usageReportsStateActions} from './slices/usage-reports-state.slice';
import {usageReportsActions} from './slices/usage-reports.slice';
import {useGetUsageReports} from './useGetUsageReports';

import type {GeneratedReport} from '../typedef';

type Props = {
    accountId: number,
    onOpenReport: (report: GeneratedReport) => void,
};

export const GeneratedUsageReports = ({accountId, onOpenReport}: Props) => {
    const dispatch = useDispatch();

    const {
        result,
        loadingError,
        pagination,
    } = useGetUsageReports({accountId});

    useEffect(() => {
        return () => {
            dispatch(usageReportsActions.clearData());
            dispatch(usageReportsStateActions.clearData());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <ServerResponseHandler serverError={loadingError}>
            <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
                <BoxLoading loading={!result}>
                    <ProfileTableHeader caption="Generated Reports" entityCaption="Report" totalCount={result?.totalCount}/>
                    {result?.data.length ? (
                        <div style={{minHeight: 66}}>
                            <GeneratedUsageReportsTable
                                rows={result.data}
                                totalCount={result?.totalCount || 0}
                                pagination={pagination}
                                onOpenReportDrawer={onOpenReport}
                            />
                        </div>
                    ) : (
                        <NoResultPage
                            title="No reports for the last 3 days to show"
                            description=""
                        />
                    )}
                </BoxLoading>
            </ErrorBoundary>
        </ServerResponseHandler>
    );
};
